import { Controller } from '@hotwired/stimulus';

/**
 * Attachment drop - files dropped anywhere on the open card, or pasted while it is open (a screenshot from
 * the clipboard), go into the attachment input and its form submits them. Pasting into a text field still
 * pastes text when the clipboard has some; files the description or comment editor already took are left alone.
 * Targets: input - the attachment file field (multiple)
 * @example
 * <div data-controller="attachment-drop" data-action="paste@window->attachment-drop#paste dragenter->attachment-drop#enter
 *      dragover->attachment-drop#over dragleave->attachment-drop#leave drop->attachment-drop#drop">
 *   <form><input type="file" multiple data-attachment-drop-target="input"></form>
 * </div>
 */
export default class extends Controller {
  static targets = ['input'];

  connect () {
    this.depth = 0;
  }

  enter (event) {
    if (!this.carriesFiles(event)) return;

    event.preventDefault();
    this.depth += 1;
    this.element.classList.add('is-dropping');
  }

  over (event) {
    if (!this.carriesFiles(event)) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  leave () {
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth === 0) this.element.classList.remove('is-dropping');
  }

  drop (event) {
    if (!this.carriesFiles(event)) return;

    this.depth = 0;
    this.element.classList.remove('is-dropping');
    if (event.defaultPrevented) return;

    event.preventDefault();
    this.upload(event.dataTransfer.files);
  }

  paste (event) {
    if (event.defaultPrevented) return;

    const clipboard = event.clipboardData;
    if (!clipboard || clipboard.files.length === 0) return;

    const field = event.target.closest?.('input, textarea, [contenteditable]');
    if (field && clipboard.getData('text/plain')) return;

    event.preventDefault();
    this.upload(clipboard.files);
  }

  upload (files) {
    if (!this.hasInputTarget || files.length === 0) return;

    const transfer = new DataTransfer();
    Array.from(files).forEach((file) => transfer.items.add(file));
    this.inputTarget.files = transfer.files;
    this.inputTarget.form.requestSubmit();
  }

  carriesFiles (event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }
}
